/**
 * Advanced State Manager
 * Provides command-based operations with dependency tracking, validation, and cascading operations
 * 
 * This sits on top of EntityManager and adds:
 * - Command execution system
 * - Dependency validation
 * - Static dependency injection
 * - Cascading deletions
 * - Rollback capability
 */

import { EntityManager, EntityType, EdgeType } from '../EntityManager';
import {
    StateCommand,
    StateTrigger,
    CommandAction,
    CommandExecutionResult,
    TriggerExecutionResult,
    ValidationResult as CommandValidationResult,
} from '@/interfaces/stateManagement/CommandInterfaces';
import {
    DependencyCheckResult,
    StaticDependencyRule,
    CascadeRule,
    DependencyContext,
    DependencyEdge,
} from '@/interfaces/stateManagement/DependencyInterfaces';
import { EdgeType as DependencyEdgeType } from '@/interfaces/entities/EdgeType';
import {
    mapDependencyEdgeTypeToEntity,
    mapEntityEdgeTypeToDependency,
} from '@/utils/edgeTypeMapper';
import {
    SystemState,
    EntityWithDependencies,
    StateChangeEvent,
    IEntityManager,
    IDependencyGraph,
} from '@/interfaces/stateManagement/StateManagerInterfaces';
import { ServiceInterface } from '@/interfaces/entities/entity_blueprints/ServiceInterface';
import { MiddlewareInterface } from '@/interfaces/entities/entity_blueprints/MiddlewareInterface';
import { EndPointInterface, EndpointAccessLevel, HttpMethod } from '@/interfaces/entities/entity_blueprints/EndPointInterface';
import { FunctionInterface, FunctionAttachmentType, ParameterType } from '@/interfaces/entities/entity_blueprints/FunctionInterface';
import { MetaDataInterface } from '@/interfaces/entities/entity_blueprints/MetaDataInterface';
import { BackendModel } from '@/data/backendViewData';
import { BUILT_IN_STATIC_DEPENDENCY_RULES } from './StaticDependencyRules';

/**
 * Advanced State Manager
 */
export class AdvancedStateManager {
    private entityManager: EntityManager;
    private staticDependencyRules: StaticDependencyRule[] = [];
    private cascadeRules: Map<EntityType, CascadeRule> = new Map();
    private subscribers: Set<(event: StateChangeEvent) => void> = new Set();
    private commandHistory: StateCommand[] = [];
    private metadata?: MetaDataInterface;

    constructor(entityManager: EntityManager) {
        this.entityManager = entityManager;
        this.setupStaticDependencyRules();
        this.setupCascadeRules();
    }

    /**
     * Execute a single command
     */
    async executeCommand(command: StateCommand): Promise<CommandExecutionResult> {
        // Validate command first
        const validation = await this.validateCommand(command);
        if (!validation.valid) {
            command.status = 'failed';
            command.error = validation.errors.map(e => e.message).join('; ');
            command.validation = validation;

            return {
                commandId: command.id || 'unknown',
                success: false,
                error: validation.errors.map(e => e.message).join('; '),
                affectedEntities: { created: [], modified: [], deleted: [] },
                edges: { created: [], removed: [] },
                warnings: validation.warnings.map(w => w.message),
            };
        }

        command.status = 'executing';
        const result: CommandExecutionResult = {
            commandId: command.id || this.generateCommandId(),
            success: true,
            affectedEntities: { created: [], modified: [], deleted: [] },
            edges: { created: [], removed: [] },
            warnings: [],
        };

        try {
            // Execute command based on action
            await this.executeCommandAction(command, result);

            // Apply static dependencies
            this.applyStaticDependenciesForCommand(command, result);

            // Mark as completed
            command.status = 'completed';
            this.commandHistory.push(command);

            // Emit event
            this.emitStateChange({
                type: 'entity_updated',
                timestamp: new Date().toISOString(),
            });

        } catch (error) {
            command.status = 'failed';
            command.error = error instanceof Error ? error.message : 'Unknown error';
            result.success = false;
            result.error = error instanceof Error ? error.message : 'Unknown error';
        }

        return result;
    }

    /**
     * Execute a trigger (chain of commands)
     */
    async executeTrigger(trigger: StateTrigger): Promise<TriggerExecutionResult> {
        const result: TriggerExecutionResult = {
            triggerId: trigger.id || this.generateTriggerId(),
            success: true,
            commandsExecuted: 0,
            commandsFailed: 0,
            results: [],
            errors: [],
        };

        trigger.status = 'executing';
        const executedCommands: CommandExecutionResult[] = [];
        const rollbackStack: (() => Promise<void>)[] = [];

        try {
            for (const command of trigger.commands) {
                const commandResult = await this.executeCommand(command);
                executedCommands.push(commandResult);
                result.results.push(commandResult);

                if (commandResult.success) {
                    result.commandsExecuted++;
                } else {
                    result.commandsFailed++;
                    result.errors?.push(commandResult.error || 'Unknown error');

                    if (trigger.stopOnError) {
                        result.success = false;
                        break;
                    }
                }
            }

            if (!result.success && trigger.rollbackOnError) {
                // Rollback executed commands (in reverse order)
                for (let i = rollbackStack.length - 1; i >= 0; i--) {
                    await rollbackStack[i]();
                }
                trigger.status = 'rolled_back';
                result.rolledBack = true;
            } else {
                trigger.status = 'completed';
            }

        } catch (error) {
            result.success = false;
            result.errors?.push(error instanceof Error ? error.message : 'Unknown error');
            trigger.status = 'failed';
        }

        return result;
    }

    /**
     * Validate command before execution
     */
    async validateCommand(command: StateCommand): Promise<CommandValidationResult> {
        const errors: { code: string; message: string }[] = [];
        const warnings: { code: string; message: string }[] = [];

        // Basic validation
        if (!command.action) {
            errors.push({ code: 'MISSING_ACTION', message: 'Command action is required' });
        }

        if (!command.target) {
            errors.push({ code: 'MISSING_TARGET', message: 'Command target is required' });
        }

        // Action-specific validation
        switch (command.action) {
            case CommandAction.REMOVE_SERVICE:
            case CommandAction.REMOVE_MIDDLEWARE:
            case CommandAction.REMOVE_ENDPOINT:
            case CommandAction.REMOVE_MODEL:
                // Check dependencies
                const dependencyCheck = this.checkDependencies(
                    this.getEntityIdFromTarget(command.target, command.action),
                    this.getEntityTypeFromAction(command.action)
                );
                if (!dependencyCheck.canDelete) {
                    errors.push({
                        code: 'DEPENDENCY_BLOCKING',
                        message: `Cannot delete: ${dependencyCheck.blockingDependencies.map(d => d.entityId).join(', ')} depend on this entity`,
                    });
                }
                break;
        }

        return {
            valid: errors.length === 0,
            errors,
            warnings,
        };
    }

    /**
     * Check dependencies before deletion
     */
    checkDependencies(entityId: string, entityType: EntityType): DependencyCheckResult {
        const blockingDependencies: { entityId: string; entityType: EntityType; relationship: string; isStatic?: boolean }[] = [];
        const warnings: string[] = [];

        // Get all edges connected to this entity
        const edges = this.entityManager.getEdgesForEntity(entityId);

        for (const edge of edges) {
            // Check if this dependency blocks deletion
            const cascadeRule = this.cascadeRules.get(entityType);
            if (cascadeRule) {
                const blockingTypes = cascadeRule.blockIfUsedBy || [];
                const edgeSourceType = this.getEntityTypeFromId(edge.sourceId);
                const edgeTargetType = this.getEntityTypeFromId(edge.targetId);

                if (edge.sourceId === entityId && blockingTypes.includes(edgeTargetType)) {
                    blockingDependencies.push({
                        entityId: edge.targetId,
                        entityType: edgeTargetType,
                        relationship: edge.edgeType,
                    });
                } else if (edge.targetId === entityId && blockingTypes.includes(edgeSourceType)) {
                    blockingDependencies.push({
                        entityId: edge.sourceId,
                        entityType: edgeSourceType,
                        relationship: edge.edgeType,
                    });
                }
            }
        }

        return {
            canDelete: blockingDependencies.length === 0,
            blockingDependencies,
            warnings,
        };
    }

    /**
     * Apply static dependencies automatically
     * This method injects common dependencies (e.g., auth middleware, file upload middleware)
     * based on entity properties and conditions defined in static dependency rules
     */
    applyStaticDependencies(entity: unknown, entityType: EntityType): void {
        const context = this.buildDependencyContext();

        // Sort rules by priority (higher priority first)
        const sortedRules = this.staticDependencyRules.sort((a, b) => (b.priority || 0) - (a.priority || 0));

        for (const rule of sortedRules) {
            try {
                // Check if rule condition is satisfied
                if (rule.condition(entity, context)) {
                    // Get edges to inject
                    const edges = rule.injectDependencies(entity, context);

                    for (const edge of edges) {
                        const entityEdgeType = mapDependencyEdgeTypeToEntity(edge.edgeType as DependencyEdgeType);

                        // Skip if edge type couldn't be mapped
                        if (!entityEdgeType) {
                            console.warn(`[Static Dependency] ⚠️  Could not map edge type for rule "${rule.name}"`);
                            continue;
                        }

                        // Check if edge already exists
                        const existingEdge = this.entityManager.findEdge(
                            edge.sourceId,
                            edge.targetId,
                            entityEdgeType
                        );

                        if (!existingEdge) {
                            // Determine source and target entity types
                            const sourceType = this.getEntityTypeFromId(edge.sourceId);
                            const targetType = this.getEntityTypeFromId(edge.targetId);

                            // Add edge to dependency graph
                            const addEdgeResult = this.entityManager.addEdge(
                                edge.sourceId,
                                edge.targetId,
                                sourceType,
                                targetType,
                                entityEdgeType
                            );

                            if (addEdgeResult.valid) {
                                console.log(`[Static Dependency] ✅ Applied rule "${rule.name}": ${edge.sourceId} → ${edge.targetId}`);

                                // Update entity arrays based on edge type
                                this.updateEntityReferencesForEdge(edge.sourceId, edge.targetId, entityEdgeType);
                            } else {
                                const errorMsg = (addEdgeResult as any).errorMessage || 'Unknown error';
                                console.warn(`[Static Dependency] ⚠️  Failed to add edge for rule "${rule.name}":`, errorMsg);
                            }
                        } else {
                            console.log(`[Static Dependency] ⏭️  Skipped rule "${rule.name}" (edge already exists)`);
                        }
                    }
                }
            } catch (error) {
                console.error(`[Static Dependency] Error applying rule "${rule.name}":`, error);
            }
        }
    }

    /**
     * Update entity reference arrays when an edge is added
     * For example, add middleware ID to endpoint's middlewareIds array
     */
    private updateEntityReferencesForEdge(sourceId: string, targetId: string, edgeType: EdgeType): void {
        switch (edgeType) {
            case EdgeType.ENDPOINT_TO_MIDDLEWARE:
                const endpoint = this.entityManager.getEndpoint(sourceId);
                if (endpoint) {
                    if (!endpoint.middlewareIds) {
                        endpoint.middlewareIds = [];
                    }
                    if (!endpoint.middlewareIds.includes(targetId)) {
                        endpoint.middlewareIds.push(targetId);
                        this.entityManager.setEndpoint(endpoint);
                        console.log(`[Static Dependency] Updated endpoint ${sourceId} middlewareIds`);
                    }
                }
                break;

            case EdgeType.MIDDLEWARE_TO_SERVICE:
                const middleware = this.entityManager.getMiddleware(sourceId);
                if (middleware) {
                    // Use servicesRequired property from MiddlewareInterface
                    if (!middleware.servicesRequired) {
                        middleware.servicesRequired = [];
                    }
                    if (!middleware.servicesRequired.includes(targetId)) {
                        middleware.servicesRequired.push(targetId);
                        this.entityManager.setMiddleware(middleware);
                        console.log(`[Static Dependency] Updated middleware ${sourceId} servicesRequired`);
                    }
                }
                break;

            case EdgeType.ENDPOINT_TO_SERVICE:
                const ep = this.entityManager.getEndpoint(sourceId);
                if (ep) {
                    if (!ep.servicesRequired) {
                        ep.servicesRequired = [];
                    }
                    if (!ep.servicesRequired.includes(targetId)) {
                        ep.servicesRequired.push(targetId);
                        this.entityManager.setEndpoint(ep);
                        console.log(`[Static Dependency] Updated endpoint ${sourceId} servicesRequired`);
                    }
                }
                break;
        }
    }

    /**
     * Cascade delete (with validation)
     */
    async cascadeDelete(entityId: string, entityType: EntityType, force: boolean = false): Promise<{ success: boolean; deletedEntities: string[]; removedEdges: string[]; errors: string[] }> {
        const result = {
            success: true,
            deletedEntities: [] as string[],
            removedEdges: [] as string[],
            errors: [] as string[],
        };

        // Check dependencies first
        if (!force) {
            const dependencyCheck = this.checkDependencies(entityId, entityType);
            if (!dependencyCheck.canDelete) {
                result.success = false;
                result.errors.push(`Cannot delete: dependencies exist`);
                return result;
            }
        }

        // Get cascade rule
        const cascadeRule = this.cascadeRules.get(entityType);
        if (cascadeRule) {
            // Execute cascade operations
            for (const cascadeTarget of cascadeRule.cascadeTo) {
                // Find entities to cascade to
                const edges = this.entityManager.getEdgesForEntity(entityId);
                for (const edge of edges) {
                    if (mapEntityEdgeTypeToDependency(edge.edgeType) === cascadeTarget.relation) {
                        const targetId = edge.sourceId === entityId ? edge.targetId : edge.sourceId;
                        const targetEntity = this.entityManager.getEntity(cascadeTarget.entityType, targetId);

                        if (targetEntity && (!cascadeTarget.condition || cascadeTarget.condition(targetEntity))) {
                            if (cascadeTarget.action === 'delete') {
                                // Delete the target entity
                                this.deleteEntity(cascadeTarget.entityType, targetId);
                                result.deletedEntities.push(targetId);
                            }

                            // Remove the edge
                            this.entityManager.removeEdge(edge.id);
                            result.removedEdges.push(edge.id);
                        }
                    }
                }
            }
        }

        // Delete the main entity
        this.deleteEntity(entityType, entityId);
        result.deletedEntities.push(entityId);

        return result;
    }

    /**
     * Get current system state snapshot
     */
    getSystemState(): SystemState {
        const dependencyEdges = this.entityManager.getAllEdges()
            .map<DependencyEdge | null>((edge) => {
                const depType = mapEntityEdgeTypeToDependency(edge.edgeType);
                if (!depType) return null;
                return {
                    ...edge,
                    edgeType: depType,
                };
            })
            .filter((edge): edge is DependencyEdge => edge !== null);

        return {
            models: this.entityManager.getModels(),
            services: this.entityManager.getServices(),
            middlewares: this.entityManager.getMiddlewares(),
            endpoints: this.entityManager.getEndpoints(),
            functions: this.entityManager.getFunctions(),
            dependencyGraph: {
                edges: dependencyEdges,
                forwardDeps: {},
                backwardDeps: {},
                timestamp: new Date().toISOString(),
            },
            metadata: this.metadata,
            timestamp: new Date().toISOString(),
        };
    }

    /**
     * Get entity with dependencies
     */
    getEntityWithDependencies(entityId: string, entityType: EntityType): EntityWithDependencies | null {
        const entity = this.entityManager.getEntity(entityType, entityId);
        if (!entity) return null;

        const edges = this.entityManager.getEdgesForEntity(entityId);
        const dependencies: string[] = [];
        const dependents: string[] = [];

        for (const edge of edges) {
            if (edge.sourceId === entityId) {
                dependencies.push(edge.targetId);
            } else {
                dependents.push(edge.sourceId);
            }
        }

        const dependencyCheck = this.checkDependencies(entityId, entityType);

        return {
            entityId,
            entityType,
            entity,
            dependencies,
            dependents,
            canDelete: dependencyCheck.canDelete,
            blockingDependencies: dependencyCheck.canDelete ? undefined : dependencyCheck,
        };
    }

    /**
     * Subscribe to state changes
     */
    subscribe(callback: (event: StateChangeEvent) => void): () => void {
        this.subscribers.add(callback);
        return () => {
            this.subscribers.delete(callback);
        };
    }

    /**
     * Reset the entire system state (clears all entities and optionally sets metadata)
     */
    resetState(metadata?: MetaDataInterface): void {
        this.entityManager.clear();
        this.metadata = metadata;
        this.emitStateChange({
            type: 'system_reset',
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Update project metadata without altering entities
     */
    setMetadata(metadata?: MetaDataInterface): void {
        const wasRBACEnabled = this.metadata?.rbac?.enabled;
        this.metadata = metadata;
        const isRBACEnabled = metadata?.rbac?.enabled;
        
        // If RBAC was just enabled, re-apply static dependencies to all endpoints
        if (!wasRBACEnabled && isRBACEnabled) {
            console.log('[AdvancedStateManager] RBAC enabled, re-applying static dependencies to all endpoints');
            this.reapplyStaticDependenciesToAllEndpoints();
        }
        
        this.emitStateChange({
            type: 'metadata_updated',
            timestamp: new Date().toISOString(),
        });
    }

    /**
     * Re-apply static dependencies to all existing endpoints
     * Useful when metadata changes (e.g., RBAC enabled) and existing endpoints need new dependencies
     */
    private reapplyStaticDependenciesToAllEndpoints(): void {
        const endpoints = this.entityManager.getEndpoints();
        console.log(`[AdvancedStateManager] Re-applying static dependencies to ${endpoints.length} endpoints`);
        
        endpoints.forEach((endpoint) => {
            const endpointWithType = endpoint as any;
            endpointWithType.entityType = EntityType.ENDPOINT;
            this.applyStaticDependencies(endpointWithType, EntityType.ENDPOINT);
        });
    }

    // ==================== PRIVATE METHODS ====================

    private async executeCommandAction(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        switch (command.action) {
            case CommandAction.ADD_METHOD:
                await this.handleAddMethod(command, result);
                break;
            case CommandAction.UPDATE_METHOD:
                await this.handleUpdateMethod(command, result);
                break;
            case CommandAction.REMOVE_METHOD:
                await this.handleRemoveMethod(command, result);
                break;
            case CommandAction.ADD_SERVICE:
                await this.handleAddService(command, result);
                break;
            case CommandAction.UPDATE_SERVICE:
                await this.handleUpdateService(command, result);
                break;
            case CommandAction.ADD_MIDDLEWARE:
                await this.handleAddMiddleware(command, result);
                break;
            case CommandAction.UPDATE_MIDDLEWARE:
                await this.handleUpdateMiddleware(command, result);
                break;
            case CommandAction.ADD_ENDPOINT:
                await this.handleAddEndpoint(command, result);
                break;
            case CommandAction.UPDATE_ENDPOINT:
                await this.handleUpdateEndpoint(command, result);
                break;
            case CommandAction.ADD_FUNCTION:
                await this.handleAddFunction(command, result);
                break;
            case CommandAction.UPDATE_FUNCTION:
                await this.handleUpdateFunction(command, result);
                break;
            case CommandAction.REMOVE_FUNCTION:
                await this.handleRemoveFunction(command, result);
                break;
            case CommandAction.REMOVE_SERVICE:
                await this.handleRemoveService(command, result);
                break;
            case CommandAction.REMOVE_MIDDLEWARE:
                await this.handleRemoveMiddleware(command, result);
                break;
            case CommandAction.REMOVE_ENDPOINT:
                await this.handleRemoveEndpoint(command, result);
                break;
            case CommandAction.ATTACH_MIDDLEWARE_TO_ENDPOINT:
                await this.handleAttachMiddlewareToEndpoint(command, result);
                break;
            case CommandAction.ATTACH_FUNCTION_TO_ENDPOINT:
                await this.handleAttachFunctionToEndpoint(command, result);
                break;
            case CommandAction.ATTACH_SERVICE_TO_ENDPOINT:
                await this.handleAttachServiceToEndpoint(command, result);
                break;
            case CommandAction.SYNC_MODEL:
                await this.handleSyncModel(command, result);
                break;
            case CommandAction.REMOVE_MODEL:
                await this.handleRemoveModel(command, result);
                break;
            case CommandAction.ADD_DEPENDENCY:
                await this.handleAddDependency(command, result);
                break;
            case CommandAction.REMOVE_DEPENDENCY:
                await this.handleRemoveDependency(command, result);
                break;
        }
    }

    private async handleAddMethod(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const serviceId = command.target.service;

        // Support multiple ways to provide the function object
        let func = command.functionObject;
        if (!func && command.data) {
            // Try data.function or data.method
            const dataFunc = (command.data as any).function || (command.data as any).method;
            if (dataFunc) {
                func = dataFunc as FunctionInterface;
            }
        }

        if (!serviceId || !func) {
            throw new Error('Service ID and function object required for addMethod. Provide functionObject or data.function');
        }

        // Auto-generate function ID if not provided
        if (!func.id && func.functionName) {
            const sanitized = func.functionName
                .replace(/[^a-zA-Z0-9]/g, '')
                .toLowerCase();
            const random = Math.random().toString(36).substring(2, 6);
            func.id = `fn_${sanitized}_${random}`;
            console.log(`[AdvancedStateManager] Auto-generated method ID: ${func.id}`);
        }

        // Set defaults for required arrays if not provided (AI might omit these)
        func.inputParameters = func.inputParameters || [];
        func.output = func.output || { parameterName: 'result', parameterType: ParameterType.ANY, required: true };
        func.implementationSteps = func.implementationSteps || [];
        func.attachmentTypes = func.attachmentTypes || [FunctionAttachmentType.SERVICE];
        func.sdkUsed = func.sdkUsed || [];
        func.servicesNeeded = func.servicesNeeded || [];
        func.externalFunctionsNeeded = func.externalFunctionsNeeded || [];
        func.description = func.description || `${func.functionName} method`;
        func.isAsync = func.isAsync !== undefined ? func.isAsync : true;

        // Add function to service
        const service = this.entityManager.getService(serviceId);
        if (!service) {
            throw new Error(`Service ${serviceId} not found`);
        }

        if (!service.functionIds) {
            service.functionIds = [];
        }
        if (!service.functionIds.includes(func.id)) {
            service.functionIds.push(func.id);
        }

        // Add to serviceFunctions array
        if (!service.serviceFunctions) {
            service.serviceFunctions = [];
        }

        // Check if function already exists, if so update it
        const existingIndex = service.serviceFunctions.findIndex(f => f.id === func.id);
        if (existingIndex >= 0) {
            service.serviceFunctions[existingIndex] = func;
        } else {
            service.serviceFunctions.push(func);
        }

        service.updated = true;
        this.entityManager.setService(service);
        result.affectedEntities.modified?.push(serviceId);
        result.affectedEntities.created?.push(func.id);

        console.log(`[AdvancedStateManager] Added method ${func.id} to service ${serviceId}`);
    }

    private async handleAddService(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const service = this.getPayload<ServiceInterface>(command, 'service');

        // Auto-generate service ID if not provided (safety net - normally handled by BackendChatInterface)
        if (!service.id && service.serviceName) {
            const sanitized = service.serviceName
                .replace(/Service$/i, '')
                .replace(/[^a-zA-Z0-9]/g, '')
                .toLowerCase();
            const random = Math.random().toString(36).substring(2, 6);
            service.id = `svc_${sanitized}_${random}`;
            console.log(`[AdvancedStateManager] Auto-generated service ID: ${service.id}`);
        }

        // Check for functions in the service payload (either deprecated serviceFunctions or implicitly needed functions)
        if (service.serviceFunctions && service.serviceFunctions.length > 0) {
            // Ensure functionIds array exists
            if (!service.functionIds) {
                service.functionIds = [];
            }

            // Sync functionIds with serviceFunctions
            for (const func of service.serviceFunctions) {
                // Add to service's functionIds if not present
                if (!service.functionIds.includes(func.id)) {
                    service.functionIds.push(func.id);
                }

                // We do NOT create separate function entities or edges anymore
            }
        }

        // Add the service to state
        const validation = this.entityManager.setService(service);
        this.ensureValid(validation, `Service ${service.id}`);
        result.affectedEntities.created?.push(service.id);
    }

    private async handleUpdateService(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const serviceId = command.target.service;
        if (!serviceId) {
            throw new Error('Service ID required for updateService');
        }
        const existing = this.entityManager.getService(serviceId);
        if (!existing) {
            throw new Error(`Service ${serviceId} not found`);
        }
        const updates = (command.data?.updates as Partial<ServiceInterface>) ||
            (command.data?.service as Partial<ServiceInterface>) || {};
        const updatedService: ServiceInterface = { ...existing, ...updates, updated: true };
        const validation = this.entityManager.setService(updatedService);
        this.ensureValid(validation, `Service ${serviceId}`);
        result.affectedEntities.modified?.push(serviceId);
    }

    private async handleAddMiddleware(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const middleware = this.getPayload<MiddlewareInterface>(command, 'middleware');

        // Auto-generate middleware ID if not provided (safety net - normally handled by BackendChatInterface)
        if (!middleware.id && middleware.middlewareName) {
            const sanitized = middleware.middlewareName
                .replace(/Middleware$/i, '')
                .replace(/[^a-zA-Z0-9]/g, '')
                .toLowerCase();
            const random = Math.random().toString(36).substring(2, 6);
            middleware.id = `mw_${sanitized}_${random}`;
            console.log(`[AdvancedStateManager] Auto-generated middleware ID: ${middleware.id}`);
        }

        // Check for embedded handler function (deprecated but possible) or if functionId is set
        if ((middleware as any).handlerFunction) {
            // If implementation steps are needed, they should be in the middleware implementationSteps
            // We do NOT create separate function entities for middleware handlers anymore
        }

        const validation = this.entityManager.setMiddleware(middleware);
        this.ensureValid(validation, `Middleware ${middleware.id}`);
        result.affectedEntities.created?.push(middleware.id);

    }

    private async handleUpdateMiddleware(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const middlewareId = command.target.middleware;
        if (!middlewareId) {
            throw new Error('Middleware ID required for updateMiddleware');
        }
        const existing = this.entityManager.getMiddleware(middlewareId);
        if (!existing) {
            throw new Error(`Middleware ${middlewareId} not found`);
        }
        const updates = (command.data?.updates as Partial<MiddlewareInterface>) ||
            (command.data?.middleware as Partial<MiddlewareInterface>) || {};
        const updatedMiddleware: MiddlewareInterface = { ...existing, ...updates, updated: true };
        const validation = this.entityManager.setMiddleware(updatedMiddleware);
        this.ensureValid(validation, `Middleware ${middlewareId}`);
        result.affectedEntities.modified?.push(middlewareId);
    }

    private async handleAddEndpoint(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const endpoint = this.getPayload<EndPointInterface>(command, 'endpoint');

        // Also check command.target.endpoint for missing data (safety net for AI variations)
        if (command.target.endpoint) {
            if (!endpoint.method && command.target.endpoint.method) {
                endpoint.method = command.target.endpoint.method;
                console.log(`[AdvancedStateManager] Copied method from target: ${endpoint.method}`);
            }
            if (!endpoint.path && command.target.endpoint.path) {
                endpoint.path = command.target.endpoint.path;
                console.log(`[AdvancedStateManager] Copied path from target: ${endpoint.path}`);
            }
            if (!endpoint.name && command.target.endpoint.name) {
                endpoint.name = command.target.endpoint.name;
                console.log(`[AdvancedStateManager] Copied name from target: ${endpoint.name}`);
            }
        }

        // Validate required fields before proceeding
        if (!endpoint.method) {
            throw new Error(`Endpoint method is required for endpoint "${endpoint.name || 'unknown'}"`);
        }
        if (!endpoint.path) {
            throw new Error(`Endpoint path is required for endpoint "${endpoint.name || 'unknown'}"`);
        }

        // Auto-generate endpoint ID if not provided (safety net - normally handled by BackendChatInterface)
        if (!endpoint.id) {
            const sanitizedPath = endpoint.path
                .replace(/^\/api\//, '')
                .replace(/[/:]/g, '_')
                .replace(/_+/g, '_')
                .replace(/^_|_$/g, '')
                .toLowerCase();
            const sanitizedMethod = endpoint.method.toLowerCase();
            const random = Math.random().toString(36).substring(2, 6);
            endpoint.id = `ep_${sanitizedMethod}_${sanitizedPath}_${random}`;
            console.log(`[AdvancedStateManager] Auto-generated endpoint ID: ${endpoint.id}`);
        }

        // Set required defaults
        if (!endpoint.accessLevel) {
            endpoint.accessLevel = EndpointAccessLevel.PUBLIC;
        }
        if (endpoint.requiresAuth === undefined) {
            endpoint.requiresAuth = false;
        }
        if (endpoint.enabled === undefined) {
            endpoint.enabled = true;
        }
        if (endpoint.isStaticGenerated === undefined) {
            endpoint.isStaticGenerated = false;
        }

        // Ensure implementationSteps is always an array
        // Note: For static-generated endpoints, steps should be set during generation
        // If they're missing, they'll need to be regenerated when the endpoint is synced
        if (!endpoint.implementationSteps) {
            endpoint.implementationSteps = [];
        }

        // Prioritize functionObject from command, but check embedded handlerFunction too
        const functionObject = command.functionObject || (endpoint as any).handlerFunction;

        if (functionObject) {
            // Ensure function ID is present or generated
            if (!functionObject.id) {
                functionObject.id = endpoint.functionId || `fn_ep_${endpoint.name}`;
            }

            // Create/Ensure function entity exists
            this.entityManager.setFunction(functionObject);
            result.affectedEntities.created?.push(functionObject.id);

            // Update endpoint references
            (endpoint as any).handlerFunction = functionObject;
            endpoint.functionId = functionObject.id;
        }

        const validation = this.entityManager.setEndpoint(endpoint);
        this.ensureValid(validation, `Endpoint ${endpoint.id}`);
        result.affectedEntities.created?.push(endpoint.id);

        if (endpoint.functionId) {
            this.entityManager.connectFunctionToEndpoint(endpoint.id, endpoint.functionId);
            const edge = this.entityManager.findEdge(endpoint.id, endpoint.functionId, EdgeType.ENDPOINT_TO_FUNCTION);
            if (edge) {
                result.edges.created?.push(edge.id);
            }
        }

        // Create model-to-endpoint edges if modelsRequired is provided
        if (endpoint.modelsRequired && endpoint.modelsRequired.length > 0) {
            for (const modelId of endpoint.modelsRequired) {
                // Verify model exists
                const model = this.entityManager.getModel(modelId);
                if (model) {
                    // Create MODEL_TO_ENDPOINT edge
                    const edgeResult = this.entityManager.addEdge(
                        modelId,
                        endpoint.id,
                        EntityType.MODEL,
                        EntityType.ENDPOINT,
                        EdgeType.MODEL_TO_ENDPOINT
                    );
                    if (edgeResult.valid) {
                        const edge = this.entityManager.findEdge(modelId, endpoint.id, EdgeType.MODEL_TO_ENDPOINT);
                        if (edge) {
                            result.edges.created?.push(edge.id);
                            console.log(`[AdvancedStateManager] Created model-to-endpoint edge: ${model.name} → ${endpoint.name}`);
                        }
                    } else {
                        console.warn(`[AdvancedStateManager] Failed to create model-to-endpoint edge for model ${modelId}:`, edgeResult.errors);
                    }
                } else {
                    console.warn(`[AdvancedStateManager] Model ${modelId} not found when creating endpoint ${endpoint.id}`);
                }
            }
        }
    }

    private async handleUpdateEndpoint(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const endpointId = command.target.endpoint?.name;
        if (!endpointId) {
            throw new Error('Endpoint ID required for updateEndpoint');
        }
        const existing = this.entityManager.getEndpoint(endpointId);
        if (!existing) {
            throw new Error(`Endpoint ${endpointId} not found`);
        }
        const updates = (command.data?.updates as Partial<EndPointInterface>) ||
            (command.data?.endpoint as Partial<EndPointInterface>) || {};
        const updatedEndpoint: EndPointInterface = { ...existing, ...updates, updated: true };
        const validation = this.entityManager.setEndpoint(updatedEndpoint);
        this.ensureValid(validation, `Endpoint ${endpointId}`);
        result.affectedEntities.modified?.push(endpointId);
    }

    private async handleAddFunction(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        // Support both functionObject at root level AND function in data (for AI compatibility)
        let functionData = command.functionObject;

        if (!functionData && command.data) {
            // Try to get from data.function (AI format)
            const dataFunc = (command.data as any).function;
            if (dataFunc) {
                functionData = dataFunc as FunctionInterface;
            }
        }

        if (!functionData) {
            throw new Error('Function object required for addFunction. Provide either command.functionObject or command.data.function');
        }

        // Auto-generate function ID if not provided (safety net - normally handled by BackendChatInterface)
        if (!functionData.id && functionData.functionName) {
            const sanitized = functionData.functionName
                .replace(/[^a-zA-Z0-9]/g, '')
                .toLowerCase();
            const random = Math.random().toString(36).substring(2, 6);
            functionData.id = `fn_${sanitized}_${random}`;
            console.log(`[AdvancedStateManager] Auto-generated function ID: ${functionData.id}`);
        }

        // Set defaults for required arrays if not provided (AI might omit these)
        functionData.inputParameters = functionData.inputParameters || [];
        functionData.output = functionData.output || { name: 'result', type: 'any', description: 'Return value' };
        functionData.implementationSteps = functionData.implementationSteps || [];
        functionData.attachmentTypes = functionData.attachmentTypes || [FunctionAttachmentType.SERVICE];
        functionData.sdkUsed = functionData.sdkUsed || [];
        functionData.servicesNeeded = functionData.servicesNeeded || [];
        functionData.externalFunctionsNeeded = functionData.externalFunctionsNeeded || [];
        functionData.description = functionData.description || `${functionData.functionName} function`;

        const validation = this.entityManager.setFunction(functionData);
        this.ensureValid(validation, `Function ${functionData.id}`);
        result.affectedEntities.created?.push(functionData.id);
    }

    private async handleUpdateFunction(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        // Support multiple ways to specify function ID
        const functionId = command.target.functionId ||
            command.functionObject?.id ||
            (command.data as any)?.function?.id;
        if (!functionId) {
            throw new Error('Function ID required for updateFunction');
        }
        const existing = this.entityManager.getFunction(functionId);
        if (!existing) {
            throw new Error(`Function ${functionId} not found`);
        }
        const updates = (command.data?.updates as Partial<FunctionInterface>) ||
            (command.data as any)?.function ||
            (command.functionObject as Partial<FunctionInterface>) || {};
        const updatedFunction: FunctionInterface = { ...existing, ...updates, id: functionId, updated: true };
        const validation = this.entityManager.setFunction(updatedFunction);
        this.ensureValid(validation, `Function ${functionId}`);
        result.affectedEntities.modified?.push(functionId);
    }

    private async handleRemoveFunction(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const functionId = command.target.functionId ||
            command.functionObject?.id ||
            (command.data as any)?.function?.id;
        if (!functionId) {
            throw new Error('Function ID required for removeFunction');
        }

        // Remove function references from all dependent entities before deletion
        this.removeFunctionReferencesFromEntities(functionId, result);

        const cascadeResult = await this.cascadeDelete(functionId, EntityType.FUNCTION, false);
        result.affectedEntities.deleted?.push(...cascadeResult.deletedEntities);
        result.edges.removed?.push(...cascadeResult.removedEdges);
        if (cascadeResult.errors.length > 0) {
            result.warnings?.push(...cascadeResult.errors);
        }
    }

    /**
     * Remove function references from all entities that depend on it
     * This cascades the deletion by removing the functionId from:
     * - Services: serviceFunctions array and functionIds array
     * - Middlewares: functionId property and functionIds array
     * - Endpoints: functionId property
     * - Other functions: externalFunctionsNeeded array
     */
    private removeFunctionReferencesFromEntities(functionId: string, result: CommandExecutionResult): void {
        // 1. Remove from all services (serviceFunctions and functionIds)
        const allServices = this.entityManager.getServices();
        for (const service of allServices) {
            let hasChanges = false;

            // Remove from serviceFunctions array
            if (service.serviceFunctions) {
                const originalLength = service.serviceFunctions.length;
                service.serviceFunctions = service.serviceFunctions.filter(f => f.id !== functionId);
                if (service.serviceFunctions.length !== originalLength) {
                    hasChanges = true;
                }
            }

            // Remove from functionIds array
            if (service.functionIds && service.functionIds.includes(functionId)) {
                service.functionIds = service.functionIds.filter(id => id !== functionId);
                hasChanges = true;
            }

            if (hasChanges) {
                this.entityManager.setService(service);
                result.affectedEntities.modified?.push(service.id);
            }
        }

        // 2. Remove from all middlewares (functionId property and functionIds array)
        const allMiddlewares = this.entityManager.getMiddlewares();
        for (const middleware of allMiddlewares) {
            let hasChanges = false;

            // Remove from deprecated functionId property
            if (middleware.functionId === functionId) {
                middleware.functionId = undefined;
                hasChanges = true;
            }

            // Remove from functionIds array
            if (middleware.functionIds && middleware.functionIds.includes(functionId)) {
                middleware.functionIds = middleware.functionIds.filter(id => id !== functionId);
                hasChanges = true;
            }

            // Also clear deprecated handlerFunction if it references this function
            if ((middleware as any).handlerFunction?.functionId === functionId) {
                (middleware as any).handlerFunction = undefined;
                hasChanges = true;
            }

            if (hasChanges) {
                this.entityManager.setMiddleware(middleware);
                result.affectedEntities.modified?.push(middleware.id);
            }
        }

        // 3. Remove from all endpoints
        const allEndpoints = this.entityManager.getEndpoints();
        for (const endpoint of allEndpoints) {
            if (endpoint.functionId === functionId) {
                endpoint.functionId = undefined;
                // Also clear deprecated handlerFunction if it references this function
                if ((endpoint as any).handlerFunction?.id === functionId) {
                    (endpoint as any).handlerFunction = undefined;
                }
                this.entityManager.setEndpoint(endpoint);
                result.affectedEntities.modified?.push(endpoint.id);
            }
        }

        // 4. Remove from other functions' externalFunctionsNeeded arrays
        const allFunctions = this.entityManager.getFunctions();
        for (const func of allFunctions) {
            if (func.externalFunctionsNeeded && func.externalFunctionsNeeded.includes(functionId)) {
                func.externalFunctionsNeeded = func.externalFunctionsNeeded.filter(id => id !== functionId);
                this.entityManager.setFunction(func);
                result.affectedEntities.modified?.push(func.id);
            }
        }
    }

    private async handleRemoveService(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const serviceId = command.target.service;
        if (!serviceId) {
            throw new Error('Service ID required for removeService');
        }

        // Remove service references from all dependent entities before deletion
        this.removeServiceReferencesFromEntities(serviceId, result);

        // Cascade delete
        const cascadeResult = await this.cascadeDelete(serviceId, EntityType.SERVICE, false);
        result.affectedEntities.deleted?.push(...cascadeResult.deletedEntities);
        result.edges.removed?.push(...cascadeResult.removedEdges);
        if (cascadeResult.errors.length > 0) {
            result.warnings?.push(...cascadeResult.errors);
        }
    }

    /**
     * Remove service references from all entities that depend on it
     * This cascades the deletion by removing the serviceId from:
     * - Functions: servicesNeeded array
     * - Middlewares: servicesRequired array
     * - Endpoints: servicesRequired array
     * - External functions (functions referenced in externalFunctionsNeeded): servicesNeeded array
     */
    private removeServiceReferencesFromEntities(serviceId: string, result: CommandExecutionResult): void {
        // 1. Remove from all functions (service functions and standalone functions)
        const allFunctions = this.entityManager.getFunctions();
        for (const func of allFunctions) {
            if (func.servicesNeeded && func.servicesNeeded.includes(serviceId)) {
                func.servicesNeeded = func.servicesNeeded.filter(id => id !== serviceId);
                // Update the function entity
                this.entityManager.setFunction(func);
                result.affectedEntities.modified?.push(func.id);
            }
        }

        // 2. Remove from all service functions (functions embedded in services)
        const allServices = this.entityManager.getServices();
        for (const service of allServices) {
            if (service.serviceFunctions) {
                let hasChanges = false;
                for (const serviceFunc of service.serviceFunctions) {
                    if (serviceFunc.servicesNeeded && serviceFunc.servicesNeeded.includes(serviceId)) {
                        serviceFunc.servicesNeeded = serviceFunc.servicesNeeded.filter(id => id !== serviceId);
                        hasChanges = true;
                    }
                }
                if (hasChanges) {
                    this.entityManager.setService(service);
                    result.affectedEntities.modified?.push(service.id);
                }
            }
        }

        // 3. Remove from all middlewares
        const allMiddlewares = this.entityManager.getMiddlewares();
        for (const middleware of allMiddlewares) {
            if (middleware.servicesRequired && middleware.servicesRequired.includes(serviceId)) {
                middleware.servicesRequired = middleware.servicesRequired.filter(id => id !== serviceId);
                // Update the middleware entity
                this.entityManager.setMiddleware(middleware);
                result.affectedEntities.modified?.push(middleware.id);
            }
        }

        // 4. Remove from all endpoints
        const allEndpoints = this.entityManager.getEndpoints();
        for (const endpoint of allEndpoints) {
            if (endpoint.servicesRequired && endpoint.servicesRequired.includes(serviceId)) {
                endpoint.servicesRequired = endpoint.servicesRequired.filter(id => id !== serviceId);
                // Update the endpoint entity
                this.entityManager.setEndpoint(endpoint);
                result.affectedEntities.modified?.push(endpoint.id);
            }
        }

        // 5. Remove from external functions (functions referenced in externalFunctionsNeeded)
        // External functions are actually function entities, so we need to check all functions
        // that are referenced in other functions' externalFunctionsNeeded arrays
        for (const func of allFunctions) {
            if (func.externalFunctionsNeeded && func.externalFunctionsNeeded.length > 0) {
                // For each external function ID, check if it's a function entity and remove service reference
                for (const externalFuncId of func.externalFunctionsNeeded) {
                    const externalFunc = this.entityManager.getFunction(externalFuncId);
                    if (externalFunc && externalFunc.servicesNeeded && externalFunc.servicesNeeded.includes(serviceId)) {
                        externalFunc.servicesNeeded = externalFunc.servicesNeeded.filter(id => id !== serviceId);
                        this.entityManager.setFunction(externalFunc);
                        result.affectedEntities.modified?.push(externalFunc.id);
                    }
                }
            }
        }
    }

    private async handleRemoveMiddleware(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const middlewareId = command.target.middleware;
        if (!middlewareId) {
            throw new Error('Middleware ID required for removeMiddleware');
        }

        // Remove middleware references from all dependent entities before deletion
        this.removeMiddlewareReferencesFromEntities(middlewareId, result);

        const cascadeResult = await this.cascadeDelete(middlewareId, EntityType.MIDDLEWARE, false);
        result.affectedEntities.deleted?.push(...cascadeResult.deletedEntities);
        result.edges.removed?.push(...cascadeResult.removedEdges);
        if (cascadeResult.errors.length > 0) {
            result.warnings?.push(...cascadeResult.errors);
        }
    }

    /**
     * Remove middleware references from all entities that depend on it
     * This cascades the deletion by removing the middlewareId from:
     * - Endpoints: middlewareIds array
     */
    private removeMiddlewareReferencesFromEntities(middlewareId: string, result: CommandExecutionResult): void {
        // Remove from all endpoints
        const allEndpoints = this.entityManager.getEndpoints();
        for (const endpoint of allEndpoints) {
            if (endpoint.middlewareIds && endpoint.middlewareIds.includes(middlewareId)) {
                endpoint.middlewareIds = endpoint.middlewareIds.filter(id => id !== middlewareId);
                // Update the endpoint entity
                this.entityManager.setEndpoint(endpoint);
                result.affectedEntities.modified?.push(endpoint.id);
            }
            // Also check deprecated middlewareRequired field for backward compatibility
            if ((endpoint as any).middlewareRequired && (endpoint as any).middlewareRequired.includes(middlewareId)) {
                (endpoint as any).middlewareRequired = (endpoint as any).middlewareRequired.filter((id: string) => id !== middlewareId);
                this.entityManager.setEndpoint(endpoint);
                result.affectedEntities.modified?.push(endpoint.id);
            }
        }
    }

    private async handleRemoveEndpoint(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const endpointId = command.target.endpoint?.name;
        if (!endpointId) {
            throw new Error('Endpoint ID required for removeEndpoint');
        }

        // Remove endpoint references from all dependent entities before deletion
        // Note: Route groups are handled in UI state, not here
        this.removeEndpointReferencesFromEntities(endpointId, result);

        const cascadeResult = await this.cascadeDelete(endpointId, EntityType.ENDPOINT, false);
        result.affectedEntities.deleted?.push(...cascadeResult.deletedEntities);
        result.edges.removed?.push(...cascadeResult.removedEdges);
        if (cascadeResult.errors.length > 0) {
            result.warnings?.push(...cascadeResult.errors);
        }
    }

    /**
     * Remove endpoint references from all entities that depend on it
     * This cascades the deletion by removing the endpointId from:
     * - Route groups: endpointIds array (handled in UI state, not here)
     * Note: Route groups are managed in UI state, so endpoint removal from route groups
     * should be handled at the UI level (BackendView component)
     */
    private removeEndpointReferencesFromEntities(endpointId: string, result: CommandExecutionResult): void {
        // Endpoints don't have direct entity references that need cleanup
        // Route groups are managed in UI state, not in EntityManager
        // The cascadeDelete method will handle edge removal
    }

    private async handleUpdateMethod(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const serviceId = command.target.service;
        const methodId = command.target.method;
        if (!serviceId || !methodId) {
            throw new Error('Service ID and method ID required for updateMethod');
        }
        const service = this.entityManager.getService(serviceId);
        if (!service || !service.serviceFunctions) {
            throw new Error(`Service ${serviceId} or method list not found`);
        }
        const functionIndex = service.serviceFunctions.findIndex((func) => func.id === methodId);
        if (functionIndex === -1) {
            throw new Error(`Method ${methodId} not found in service ${serviceId}`);
        }
        const updates = (command.data?.updates as Partial<FunctionInterface>) ||
            (command.functionObject as Partial<FunctionInterface>) || {};
        const updatedFunction: FunctionInterface = {
            ...service.serviceFunctions[functionIndex],
            ...updates,
            id: methodId,
            updated: true,
        };
        service.serviceFunctions[functionIndex] = updatedFunction;
        service.updated = true;
        const serviceValidation = this.entityManager.setService(service);
        this.ensureValid(serviceValidation, `Service ${serviceId}`);
        // We do not update separate function entity as it doesn't exist
        result.affectedEntities.modified?.push(serviceId);
    }

    private async handleRemoveMethod(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const serviceId = command.target.service;
        const methodId = command.target.method;
        if (!serviceId || !methodId) {
            throw new Error('Service ID and method ID required for removeMethod');
        }
        const service = this.entityManager.getService(serviceId);
        if (!service) {
            throw new Error(`Service ${serviceId} not found`);
        }
        service.serviceFunctions = (service.serviceFunctions || []).filter((func) => func.id !== methodId);
        if (service.functionIds) {
            service.functionIds = service.functionIds.filter((id) => id !== methodId);
        }
        service.updated = true;
        const validation = this.entityManager.setService(service);
        this.ensureValid(validation, `Service ${serviceId}`);
        // We do not delete separate function entity as it doesn't exist
        result.affectedEntities.modified?.push(serviceId);
    }

    /**
     * Find an endpoint by ID or by name (fallback for AI-generated commands)
     */
    private findEndpointByIdOrName(idOrName: string): EndPointInterface | undefined {
        // First try direct lookup by ID
        const byId = this.entityManager.getEndpoint(idOrName);
        if (byId) return byId;

        // Fallback: search by name (case-insensitive)
        const allEndpoints = this.entityManager.getEndpoints();
        return allEndpoints.find(ep =>
            ep.name?.toLowerCase() === idOrName.toLowerCase() ||
            ep.id === idOrName
        );
    }

    /**
     * Find a middleware by ID or by name (fallback for AI-generated commands)
     */
    private findMiddlewareByIdOrName(idOrName: string): MiddlewareInterface | undefined {
        const byId = this.entityManager.getMiddleware(idOrName);
        if (byId) return byId;

        const allMiddlewares = this.entityManager.getMiddlewares();
        return allMiddlewares.find(mw =>
            mw.middlewareName?.toLowerCase() === idOrName.toLowerCase() ||
            mw.id === idOrName
        );
    }

    /**
     * Find a service by ID or by name (fallback for AI-generated commands)
     */
    private findServiceByIdOrName(idOrName: string): ServiceInterface | undefined {
        const byId = this.entityManager.getService(idOrName);
        if (byId) return byId;

        const allServices = this.entityManager.getServices();
        return allServices.find(svc =>
            svc.serviceName?.toLowerCase() === idOrName.toLowerCase() ||
            svc.id === idOrName
        );
    }

    private async handleAttachMiddlewareToEndpoint(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const endpointIdOrName = command.target.endpoint?.name;
        const middlewareIdOrName = this.getPayload<string>(command, 'middlewareId');
        if (!endpointIdOrName) {
            throw new Error('Endpoint ID required for attachMiddleware');
        }
        if (!middlewareIdOrName) {
            throw new Error('Middleware ID required for attachMiddleware');
        }
        const endpoint = this.findEndpointByIdOrName(endpointIdOrName);
        if (!endpoint) {
            const available = this.entityManager.getEndpoints().map(e => `${e.id} ("${e.name}")`).join(', ');
            throw new Error(`Endpoint "${endpointIdOrName}" not found. Available endpoints: ${available || 'none'}`);
        }
        // Validate middleware exists
        const middleware = this.findMiddlewareByIdOrName(middlewareIdOrName);
        if (!middleware) {
            const available = this.entityManager.getMiddlewares().map(m => `${m.id} ("${m.middlewareName}")`).join(', ');
            throw new Error(`Middleware "${middlewareIdOrName}" not found. Available middlewares: ${available || 'none'}`);
        }
        const middlewareIds = new Set(endpoint.middlewareIds || []);
        middlewareIds.add(middleware.id); // Use actual middleware ID
        endpoint.middlewareIds = Array.from(middlewareIds);
        endpoint.updated = true;
        const validation = this.entityManager.setEndpoint(endpoint);
        this.ensureValid(validation, `Endpoint ${endpoint.id}`);
        const edgeResult = this.entityManager.connectMiddlewareToEndpoint(endpoint.id, middleware.id);
        this.ensureValid(edgeResult, `Attach middleware ${middleware.id}`);
        const edge = this.entityManager.findEdge(endpoint.id, middleware.id, EdgeType.ENDPOINT_TO_MIDDLEWARE);
        if (edge) {
            result.edges.created?.push(edge.id);
        }
        result.affectedEntities.modified?.push(endpoint.id);
    }

    /**
     * Find a function by ID or by name (fallback for AI-generated commands)
     */
    private findFunctionByIdOrName(idOrName: string): FunctionInterface | undefined {
        const byId = this.entityManager.getFunction(idOrName);
        if (byId) return byId;

        const allFunctions = this.entityManager.getFunctions();
        return allFunctions.find(fn =>
            fn.functionName?.toLowerCase() === idOrName.toLowerCase() ||
            fn.id === idOrName
        );
    }

    private async handleAttachFunctionToEndpoint(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const endpointIdOrName = command.target.endpoint?.name;
        if (!endpointIdOrName) {
            throw new Error('Endpoint ID required for attachFunction');
        }
        const endpoint = this.findEndpointByIdOrName(endpointIdOrName);
        if (!endpoint) {
            const available = this.entityManager.getEndpoints().map(e => `${e.id} ("${e.name}")`).join(', ');
            throw new Error(`Endpoint "${endpointIdOrName}" not found. Available endpoints: ${available || 'none'}`);
        }
        let functionIdOrName = (command.data?.functionId as string) || command.functionObject?.id;

        // If a functionObject is provided, create/update the function
        if (command.functionObject) {
            const validation = this.entityManager.setFunction(command.functionObject);
            this.ensureValid(validation, `Function ${command.functionObject.id}`);
            functionIdOrName = command.functionObject.id;
        }

        if (!functionIdOrName) {
            throw new Error('Function ID required for attachFunction');
        }

        // Validate function exists (or was just created)
        const func = this.findFunctionByIdOrName(functionIdOrName);
        if (!func && !command.functionObject) {
            const available = this.entityManager.getFunctions().map(f => `${f.id} ("${f.functionName}")`).join(', ');
            throw new Error(`Function "${functionIdOrName}" not found. Available functions: ${available || 'none'}. Either provide an existing function ID or include functionObject to create one.`);
        }

        const actualFunctionId = func?.id || functionIdOrName;
        endpoint.functionId = actualFunctionId;
        (endpoint as any).handlerFunction = func || command.functionObject;
        endpoint.updated = true;
        const validation = this.entityManager.setEndpoint(endpoint);
        this.ensureValid(validation, `Endpoint ${endpoint.id}`);
        const resultEdge = this.entityManager.connectFunctionToEndpoint(endpoint.id, actualFunctionId);
        this.ensureValid(resultEdge, `Attach function ${actualFunctionId}`);
        const edge = this.entityManager.findEdge(endpoint.id, actualFunctionId, EdgeType.ENDPOINT_TO_FUNCTION);
        if (edge) {
            result.edges.created?.push(edge.id);
        }
        result.affectedEntities.modified?.push(endpoint.id);
    }

    private async handleAttachServiceToEndpoint(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const endpointIdOrName = command.target.endpoint?.name;
        const serviceIdOrName = this.getPayload<string>(command, 'serviceId');
        if (!endpointIdOrName) {
            throw new Error('Endpoint ID required for attachService');
        }
        if (!serviceIdOrName) {
            throw new Error('Service ID required for attachService');
        }
        const endpoint = this.findEndpointByIdOrName(endpointIdOrName);
        if (!endpoint) {
            const available = this.entityManager.getEndpoints().map(e => `${e.id} ("${e.name}")`).join(', ');
            throw new Error(`Endpoint "${endpointIdOrName}" not found. Available endpoints: ${available || 'none'}`);
        }
        // Validate service exists
        const service = this.findServiceByIdOrName(serviceIdOrName);
        if (!service) {
            const available = this.entityManager.getServices().map(s => `${s.id} ("${s.serviceName}")`).join(', ');
            throw new Error(`Service "${serviceIdOrName}" not found. Available services: ${available || 'none'}`);
        }
        const servicesRequired = new Set(endpoint.servicesRequired || []);
        servicesRequired.add(service.id); // Use actual service ID
        endpoint.servicesRequired = Array.from(servicesRequired);
        endpoint.updated = true;
        const validation = this.entityManager.setEndpoint(endpoint);
        this.ensureValid(validation, `Endpoint ${endpoint.id}`);
        result.affectedEntities.modified?.push(endpoint.id);
    }

    private async handleSyncModel(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const model = this.getPayload<BackendModel>(command, 'model');
        const validation = this.entityManager.setModel(model);
        this.ensureValid(validation, `Model ${model.id}`);
        result.affectedEntities.created?.push(model.id);
    }

    private async handleRemoveModel(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const modelId = command.target.model;
        if (!modelId) {
            throw new Error('Model ID required for removeModel');
        }
        const cascadeResult = await this.cascadeDelete(modelId, EntityType.MODEL, false);
        result.affectedEntities.deleted?.push(...cascadeResult.deletedEntities);
        result.edges.removed?.push(...cascadeResult.removedEdges);
        if (cascadeResult.errors.length > 0) {
            result.warnings?.push(...cascadeResult.errors);
        }
    }

    private async handleAddDependency(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const data = command.data as {
            sourceId?: string;
            targetId?: string;
            sourceType?: EntityType;
            targetType?: EntityType;
            edgeType?: EdgeType;
            metadata?: Record<string, unknown>;
        } | undefined;
        if (!data?.sourceId || !data?.targetId || !data?.edgeType) {
            throw new Error('sourceId, targetId, and edgeType are required for addDependency');
        }
        const validation = this.entityManager.addEdge(
            data.sourceId,
            data.targetId,
            data.sourceType || this.getEntityTypeFromId(data.sourceId),
            data.targetType || this.getEntityTypeFromId(data.targetId),
            data.edgeType,
            data.metadata
        );
        this.ensureValid(validation, 'Add dependency');
        const edge = this.entityManager.findEdge(data.sourceId, data.targetId, data.edgeType);
        if (edge) {
            result.edges.created?.push(edge.id);
        }
    }

    private async handleRemoveDependency(command: StateCommand, result: CommandExecutionResult): Promise<void> {
        const data = command.data as { sourceId?: string; targetId?: string; edgeType?: EdgeType } | undefined;
        if (!data?.sourceId || !data?.targetId) {
            throw new Error('sourceId and targetId required for removeDependency');
        }
        const removed = this.entityManager.disconnect(data.sourceId, data.targetId, data.edgeType);
        if (!removed) {
            throw new Error(`Dependency between ${data.sourceId} and ${data.targetId} not found`);
        }
        const edgeId = `${data.sourceId}->${data.targetId}${data.edgeType ? `:${data.edgeType}` : ''}`;
        result.edges.removed?.push(edgeId);
    }

    /**
     * Apply static dependencies for a command
     * This automatically injects common dependencies based on entity properties
     */
    private applyStaticDependenciesForCommand(command: StateCommand, result: CommandExecutionResult): void {
        // Determine which entity was created/modified
        let entity: unknown = null;
        let entityType: EntityType | null = null;

        switch (command.action) {
            case CommandAction.ADD_ENDPOINT:
            case CommandAction.UPDATE_ENDPOINT:
                const endpointId = command.target.endpoint?.name || result.affectedEntities.created?.[0];
                if (endpointId) {
                    entity = this.entityManager.getEndpoint(endpointId);
                    entityType = EntityType.ENDPOINT;
                }
                break;

            case CommandAction.ADD_MIDDLEWARE:
            case CommandAction.UPDATE_MIDDLEWARE:
                const middlewareId = command.target.middleware || result.affectedEntities.created?.[0];
                if (middlewareId) {
                    entity = this.entityManager.getMiddleware(middlewareId);
                    entityType = EntityType.MIDDLEWARE;
                }
                break;

            case CommandAction.ADD_SERVICE:
            case CommandAction.UPDATE_SERVICE:
                const serviceId = command.target.service || result.affectedEntities.created?.[0];
                if (serviceId) {
                    entity = this.entityManager.getService(serviceId);
                    entityType = EntityType.SERVICE;
                }
                break;
        }

        if (entity && entityType) {
            // Cast entity to have entityType property for static dependency rules
            const entityWithType = entity as any;
            entityWithType.entityType = entityType;

            this.applyStaticDependencies(entityWithType, entityType);
        }
    }

    private setupStaticDependencyRules(): void {
        // Load built-in static dependency rules
        this.staticDependencyRules = [...BUILT_IN_STATIC_DEPENDENCY_RULES];
        console.log(`[AdvancedStateManager] Loaded ${this.staticDependencyRules.length} static dependency rules`);
    }

    private setupCascadeRules(): void {
        // Service Cascade Rules
        this.cascadeRules.set(EntityType.SERVICE, {
            cascadeTo: [
                {
                    entityType: EntityType.FUNCTION,
                    relation: DependencyEdgeType.SERVICE_TO_FUNCTION,
                    action: 'delete',
                    condition: () => true // Delete functions attached to this service
                }
            ],
            blockIfUsedBy: [
                EntityType.ENDPOINT,
                EntityType.MIDDLEWARE,
                EntityType.SERVICE
            ]
        });

        // Middleware Cascade Rules
        this.cascadeRules.set(EntityType.MIDDLEWARE, {
            cascadeTo: [
                {
                    entityType: EntityType.FUNCTION,
                    relation: DependencyEdgeType.MIDDLEWARE_TO_FUNCTION,
                    action: 'delete',
                    condition: () => true
                }
            ],
            blockIfUsedBy: [
                EntityType.ENDPOINT
            ]
        });

        // Endpoint Cascade Rules
        this.cascadeRules.set(EntityType.ENDPOINT, {
            cascadeTo: [
                {
                    entityType: EntityType.FUNCTION,
                    relation: DependencyEdgeType.ENDPOINT_TO_FUNCTION,
                    action: 'delete',
                    condition: () => true
                }
            ],
            blockIfUsedBy: []
        });

        // Function Cascade Rules (Functions don't cascade, they are blocked)
        this.cascadeRules.set(EntityType.FUNCTION, {
            cascadeTo: [],
            blockIfUsedBy: [
                EntityType.SERVICE,
                EntityType.MIDDLEWARE,
                EntityType.ENDPOINT
            ]
        });

        // Model Cascade Rules
        this.cascadeRules.set(EntityType.MODEL, {
            cascadeTo: [],
            blockIfUsedBy: [
                EntityType.ENDPOINT,
                EntityType.SERVICE,
                EntityType.MIDDLEWARE
            ]
        });
    }

    private buildDependencyContext(): DependencyContext {
        const dependencyEdges = this.entityManager.getAllEdges()
            .map<DependencyEdge | null>((edge) => {
                const depType = mapEntityEdgeTypeToDependency(edge.edgeType);
                if (!depType) return null;
                return {
                    ...edge,
                    edgeType: depType,
                };
            })
            .filter((edge): edge is DependencyEdge => edge !== null);

        // Create a context with compatible structure
        // EntityManager will be cast to IEntityManager at usage site since it has most methods
        const context: DependencyContext = {
            entityManager: {
                getModels: () => this.entityManager.getModels(),
                getServices: () => this.entityManager.getServices(),
                getMiddlewares: () => this.entityManager.getMiddlewares(),
                getEndpoints: () => this.entityManager.getEndpoints(),
                getFunctions: () => this.entityManager.getFunctions(),
                getEntity: (entityId: string, entityType: EntityType) =>
                    this.entityManager.getEntity(entityType, entityId),
                addEntity: () => { }, // Not used in static rules
                updateEntity: () => { }, // Not used in static rules
                deleteEntity: () => { }, // Not used in static rules
                getMetaData: () => this.metadata, // Add metadata access for static rules
            } as IEntityManager & { getMetaData?: () => MetaDataInterface  },
            models: this.entityManager.getModels(),
            metadata: this.metadata, // Also include metadata directly in context
        };

        return context;
    }

    private getPayload<T>(command: StateCommand, key: string): T {
        const payload = command.data?.[key] as T | undefined;
        if (!payload) {
            throw new Error(`${key} data is required for ${command.action}`);
        }
        return payload;
    }

    private ensureValid(validation: { valid: boolean; errors: string[] }, context: string): void {
        if (!validation.valid) {
            const message = validation.errors.join(', ') || 'Unknown validation error';
            throw new Error(`${context} validation failed: ${message}`);
        }
    }

    private deleteEntity(entityType: EntityType, entityId: string): boolean {
        switch (entityType) {
            case EntityType.SERVICE:
                return this.entityManager.deleteService(entityId);
            case EntityType.MIDDLEWARE:
                return this.entityManager.deleteMiddleware(entityId);
            case EntityType.ENDPOINT:
                return this.entityManager.deleteEndpoint(entityId);
            case EntityType.FUNCTION:
                return this.entityManager.deleteFunction(entityId);
            case EntityType.MODEL:
                return this.entityManager.deleteModel(entityId);
            default:
                return false;
        }
    }

    private getEntityIdFromTarget(target: StateCommand['target'], action: CommandAction): string {
        if (target.service) return target.service;
        if (target.middleware) return target.middleware;
        if (target.endpoint) return target.endpoint.name;
        if (target.functionId) return target.functionId;
        if (target.model) return target.model;
        throw new Error('Could not extract entity ID from target');
    }

    private getEntityTypeFromAction(action: CommandAction): EntityType {
        const actionLower = action.toLowerCase();
        if (actionLower.includes('service')) return EntityType.SERVICE;
        if (actionLower.includes('middleware')) return EntityType.MIDDLEWARE;
        if (actionLower.includes('endpoint')) return EntityType.ENDPOINT;
        if (actionLower.includes('function')) return EntityType.FUNCTION;
        if (actionLower.includes('model')) return EntityType.MODEL;
        throw new Error('Could not determine entity type from action');
    }

    private getEntityTypeFromId(entityId: string): EntityType {
        // Try to determine entity type by checking which map contains the ID
        if (this.entityManager.getService(entityId)) return EntityType.SERVICE;
        if (this.entityManager.getMiddleware(entityId)) return EntityType.MIDDLEWARE;
        if (this.entityManager.getEndpoint(entityId)) return EntityType.ENDPOINT;
        if (this.entityManager.getFunction(entityId)) return EntityType.FUNCTION;
        if (this.entityManager.getModel(entityId)) return EntityType.MODEL;
        throw new Error(`Could not determine entity type for ID: ${entityId}`);
    }

    private generateCommandId(): string {
        return `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    private generateTriggerId(): string {
        return `trigger_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }

    private emitStateChange(event: Partial<StateChangeEvent>): void {
        const fullEvent: StateChangeEvent = {
            ...event,
            timestamp: event.timestamp || new Date().toISOString(),
        } as StateChangeEvent;

        this.subscribers.forEach(callback => {
            try {
                callback(fullEvent);
            } catch (error) {
                console.error('Error in state change subscriber:', error);
            }
        });
    }
}

